import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Box, Button, Radio, FormControlLabel, RadioGroup } from '@mui/material';
import './TiersCard.scss';
import LockIcon from '@mui/icons-material/Lock';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const TiersCard = ({ tier, backgroundColor, textColor, handleSelection, interval, intervalInfo, selectedPlayers = [], lastCard, hasDeadlinePassed }) => {
    // Find selected player for this tier
    const tierSelectedPlayers = selectedPlayers.filter(pick => 
        pick.interval === interval && pick.tier === tier.tier
    );
    //console.log("Tier Selected Players", tierSelectedPlayers, "Selected Players", selectedPlayers, "Interval", interval, "Interval Options", intervalInfo);
    const picksThisTier = tier.tierPicks || 1;
    const maxSelectionsReached = tierSelectedPlayers.length >= picksThisTier;
    const hasSelection = tierSelectedPlayers.length > 0;

    const hasPlayerBeenPreviouslySelected = (playerId, interval) => {
        // Check if the player has been selected in the past intervals
        const alreadySelected =  selectedPlayers?.some(pick =>
            String(pick.id) === String(playerId) && pick.interval < interval
        );
        if (!alreadySelected) return false;
        // Check which interval the player was selected in
        const selectedInterval = selectedPlayers.find(pick => 
            String(pick.id) === String(playerId) && pick.interval < interval
        )?.interval;
        // return true and the interval if the player was selected in the past
        return {alreadySelected, selectedInterval};
    }; 
    
    
    // Initialize cardOpen state
    const [cardOpen, setCardOpen] = useState(!maxSelectionsReached);
    
    // Function to identify the worst 2 picks for the current interval
    const worstPicksForInterval = useMemo(() => {
        // Only calculate this if we have selectedPlayers for this interval
        const intervalPicks = selectedPlayers.filter(pick => pick.interval === interval);
        
        if (!intervalPicks || intervalPicks.length === 0) return [];
        
        // Sort picks by score (higher is worse in golf)
        // If scores are tied, sort by tier (higher tier is worse if scores are tied)
        const sortedPicks = [...intervalPicks].sort((a, b) => {
            // Get the scores, defaulting to 0 if undefined/null
            const scoreA = (a.overallScore !== undefined && a.overallScore !== null) ? a.overallScore : 0;
            const scoreB = (b.overallScore !== undefined && b.overallScore !== null) ? b.overallScore : 0;
            
            // If scores are different, sort by score (higher is worse)
            if (scoreA !== scoreB) {
                return scoreB - scoreA;
            }
            
            // If scores are tied, sort by tier (higher tier is worse)
            return b.tier - a.tier;
        });
        
        // Get the worst 2 picks (or fewer if there aren't 2 picks total)
        return sortedPicks.slice(0, Math.min(2, sortedPicks.length));
    }, [selectedPlayers, interval]);
    
    // Function to check if a pick is one of the worst 2
    const isWorstPick = (playerId) => {
        return worstPicksForInterval.some(pick => String(pick.id) === String(playerId));
    };
    
    // Update cardOpen state when interval changes
    useEffect(() => {
        // Always keep the card open when there's no selection
        // For tiers with existing selections, follow the maxSelectionsReached rule
        setCardOpen(!hasSelection || !maxSelectionsReached);
    }, [interval, hasSelection, maxSelectionsReached]);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleSelectClick = () => {
        // Allow opening the box even when deadline has passed
        setCardOpen(!cardOpen);
    };
    
    const tierText = `Tier ${tier?.tier}`;
    // Header Text (if it starts with "Pick" then return "", otherwise return the tier text)
    const headerText = tier?.text?.startsWith("Pick") ? "" : tier?.text || "";
    const numberOfPicks = tier?.picks || 1;
    const selectText = hasDeadlinePassed ? "Times Picked" : `Select ${numberOfPicks} Golfer${numberOfPicks > 1 ? 's' : ''}`;
    const players = tier?.players || [];
    //console.log("TIER", tier)
    

    // If a selection has already been made, change the button text
    const buttonText = cardOpen ? "Close" : (hasDeadlinePassed ? "Open" : "Edit");
    
    // Get the selected player's full info
    const selectedPlayerInfo = hasSelection 
        ? players.find(player => String(player.id) === String(tierSelectedPlayers[0]?.id))
        : null;
    
    const myPickText = hasSelection 
        ? tierSelectedPlayers[0].id.startsWith('no-pick') 
            ? "No Pick" 
            : selectedPlayerInfo?.name || "No Pick"
        : "No Pick";

    // Format the player's score if available
    const getPlayerScore = (player) => {
        if (!player) return null;
        
        if (player.withdrawn) {
            return 'WD';
        } else if (player.cut) {
            return 'Cut';
        } else if (player.overallScore === undefined || player.overallScore === null || player.overallScore === 0) {
            return 'E';
        } else if (player.overallScore > 0) {
            return `+${player.overallScore}`;
        } else {
            return player.overallScore;
        }
    };

    // Get the score for the selected player
    const myPickScore = tierSelectedPlayers.length > 0 ? getPlayerScore(tierSelectedPlayers[0]) : null;
    
    // Check if my pick is one of the worst 2 - only if interval has started
    const isMyPickOneOfWorst = useMemo(() => {
        if (!hasSelection || !tierSelectedPlayers[0] || !hasDeadlinePassed) return false;
        return isWorstPick(tierSelectedPlayers[0].id);
    }, [hasSelection, tierSelectedPlayers, isWorstPick, hasDeadlinePassed]);

    const handlePlayerSelection = (event) => {
        // Do not allow selection if deadline has passed
        if (hasDeadlinePassed) return;
        
        const id = event.target.value;
        
        // Close the card if picks for this tier have been met
        if (maxSelectionsReached) {
            setCardOpen(false);
        }
        
        handleSelection(interval, tier.tier, picksThisTier, id);
    };

    const isPlayerSelected = (id) => {
        return tierSelectedPlayers.some(pick => String(pick.id) === String(id));
    };

    return (
        <Box 
            className="tiers-card-container"
            sx={{
                marginBottom: lastCard ? '60px' : '0',
            }}
        >
            <Box 
                className="tiers-card-top-border"
                sx={{ backgroundColor: backgroundColor }}
            >
                <Typography className="tiers-card-top-border-left-text" sx={{ color: textColor}}>{tierText}</Typography>
                <Typography className='tiers-card-top-border-right-text' sx={{ color: textColor}}>{headerText}</Typography>
                
                {/* Show the button if a selection has been made */}
                {hasSelection && (
                    <Button
                        sx={{
                            padding: '8px',
                            backgroundColor: '#00AAD1',
                            height: '22px',
                            color: '#fff',
                            '&:hover': {
                                backgroundColor: '#00AAD1',
                            },
                        }}
                        onClick={handleSelectClick}
                    >
                        {buttonText}
                    </Button>
                )}
            </Box>

            {/* Display selectable radio group when deadline has not passed */}
            {cardOpen && !hasDeadlinePassed && (
                <Box className="tiers-card-list">
                    <Typography className="tiers-card-bottom-border-text">{selectText}</Typography>
                    <RadioGroup 
                        value={tierSelectedPlayers.length > 0 ? tierSelectedPlayers[0].id : ""}
                        onChange={handlePlayerSelection}
                    >
                        {players.map((player) => {
                            const playerSelection = hasPlayerBeenPreviouslySelected(player.id, interval);
                            const isPreviouslySelected = playerSelection?.alreadySelected || false;
                            const selectedIntervalNum = playerSelection?.selectedInterval;
                            //console.log("IS Previously Selected", isPreviouslySelected, "Selected Interval Num", selectedIntervalNum);
                            
                            // Get the tournament name for the selected interval
                            const tournamentName = isPreviouslySelected && intervalInfo 
                                ? intervalInfo?.info?.find(i => i.interval === selectedIntervalNum)?.shortLabel
                                : '';
                                //console.log("Tournament Name", tournamentName, "Interval Options", intervalInfo);
                            
                            return (
                                <Box 
                                    key={`${player.id}-${tier.tier}`}
                                    sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center',
                                        position: 'relative' 
                                    }}
                                >
                                    <FormControlLabel
                                        value={player.id}
                                        control={<Radio disabled={isPreviouslySelected} />}
                                        label={[
                                            player.logo && `${player.logo} `,
                                            player.rank && `(${player.rank})`,
                                            player.name,
                                            player.record && `${player.record}`
                                        ].filter(Boolean).join(" ")}
                                        
                                        sx={{
                                            height: '30px',
                                            width: '100%',
                                            '& .MuiFormControlLabel-label': {
                                                fontSize: isMobile ? '12px' : '14px',
                                                color: isPreviouslySelected ? '#888888' : 'inherit',
                                                textDecoration: isPreviouslySelected ? 'line-through' : 'none'
                                            }
                                        }}
                                    />
                                    {isPreviouslySelected && tournamentName && (
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            position: 'absolute', 
                                            right: '12px',
                                            color: '#888888'
                                        }}>
                                            <Typography variant="caption" sx={{ marginRight: '4px' }}>
                                                {tournamentName}
                                            </Typography>
                                            <LockIcon fontSize="small" />
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </RadioGroup>
                </Box>
            )}
            
            {/* Display player list without radio buttons when deadline has passed */}
            {hasDeadlinePassed && cardOpen && (
                <Box className="tiers-card-list">
                    <Typography className="tiers-card-bottom-border-text">{selectText}</Typography>
                    {players.map((player) => {
                         const playerSelection = hasPlayerBeenPreviouslySelected(player.id, interval);
                         const isPreviouslySelected = playerSelection?.alreadySelected || false;
                         const selectedIntervalNum = playerSelection?.selectedInterval;
                         
                         // Get the tournament name for the selected interval
                         const tournamentName = isPreviouslySelected && intervalInfo?.info 
                             ? intervalInfo.info.find(i => i.interval === selectedIntervalNum)?.shortLabel
                             : '';
                        
                        return (
                            <Box 
                                key={`${player.id}-${tier.tier}`}
                                sx={{
                                    padding: '8px 16px',
                                    fontSize: isMobile ? '12px' : '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    backgroundColor: isPlayerSelected(player.id) ? 'rgba(0, 170, 209, 0.1)' : 'transparent',
                                    color: isPlayerSelected(player.id) ? '#00AAD1' : 'inherit',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', flex: '1' }}>
                                    {isPlayerSelected(player.id) && <CheckIcon sx={{ marginRight: '8px', fontSize: '16px', color: '#00AAD1' }} />}
                                    <span style={{ 
                                        paddingLeft: isPlayerSelected(player.id) ? '0' : '24px',
                                        fontWeight: isPlayerSelected(player.id) ? 800 : 400,
                                        color: isPreviouslySelected ? '#888888' : 'inherit',
                                        textDecoration: isPreviouslySelected ? 'line-through' : 'none'
                                    }}>
                                        {[
                                            player.logo && `${player.logo} `,
                                            player.rank && `(${player.rank})`,
                                            player.name,
                                            player.record && `${player.record}`
                                        ].filter(Boolean).join(" ")}
                                    </span>
                                    {isPreviouslySelected && (
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            marginLeft: '12px',
                                            color: '#888888'
                                        }}>
                                            <LockIcon fontSize="small" />
                                        </Box>
                                    )}
                                </Box>
                                {hasDeadlinePassed && (
                                    <Box sx={{ 
                                        textAlign: 'right', 
                                        fontWeight: 700,
                                        color: isPlayerSelected(player.id) ? '#00AAD1' : '#002129',
                                    }}>
                                        {player.pickCount ?? 0}
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}

            <Box className="tiers-card-bottom-border">
                <Typography className="tiers-card-bottom-border-text">
                    My Pick:&nbsp;
                    <Box component="span" sx={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        color: myPickText === "No Pick" ? "#CC293C" : (isMyPickOneOfWorst ? "#CC293C" : "#002129")
                    }}>
                        {myPickText}
                        {myPickText !== "No Pick" && myPickScore && (
                            <span style={{ 
                                marginLeft: '4px',
                                color: isMyPickOneOfWorst ? "#CC293C" : "#002129",
                                fontWeight: 700
                            }}>
                                ({myPickScore})
                            </span>
                        )}
                    </Box>
                    {myPickText === "No Pick" && (
                        <CloseIcon sx={{ color: "#CC293C", marginTop: '-2px' }} />
                    )}
                </Typography>
            </Box>
        </Box>
    );
};

export default TiersCard;